import {
  AspectRatio,
  useDisclosure,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Link,
  Spacer,
  Text,
  useBreakpointValue,
  VStack,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { parseDateTime } from "@internationalized/date";
import PampaImage from "@raiden/library/components/PampaImage";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { CalendarModal } from "../../components/CalendarModal";
import { DatePicker } from "../../components/DatePicker";
import { Icon } from "../../components/Icon";
import { ModalToSeeCovers } from "../../components/ModalToSeeCovers";
import { CreateBookings } from "./Create";
import { ViewBookings } from "./View";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 */
/**
 * @param {Props} props
 */
export function CommunityHallsItem({ rentalArea }) {
  const translate = useTranslate();

  const intl = useIntl();

  const format =
    useBreakpointValue({ base: "default", lg: "large" }) ?? "default";
  const offset = useBreakpointValue({ base: 80, lg: 160 }) ?? 100;

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();

  const [date, setDate] = useState("");

  const [currentCover, setCurrentCover] = useState(0);

  function nextCover() {
    setCurrentCover((currentCover) => currentCover + 1);
  }

  function previousCover() {
    setCurrentCover((currentCover) => currentCover - 1);
  }

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset; // Pixel to go under the header
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
  }, [offset]);

  return (
    <Box w="full" id={`rentalArea-${rentalArea.id}`}>
      <Flex
        direction={{ base: "column", lg: "row" }}
        p=".5rem"
        bg="brandPrimary.700"
        borderRadius="4px 4px 0 0"
        align={{ base: "start", lg: "center" }}
        gap="1rem">
        <VStack align="start" spacing=".25rem">
          <Text
            fontSize="1.25rem"
            fontWeight={400}
            lineHeight="1.75rem"
            color="white">
            {translate(rentalArea.name)}
          </Text>

          <HStack>
            <Icon icon="ms_place" size="1.25rem" color="gray.400" />

            <Text fontWeight={400} color="white" textDecoration="underline">
              {rentalArea.lat && rentalArea.lng ? (
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${rentalArea.lat},${rentalArea.lng}`}
                  isExternal>
                  <FormattedMessage
                    defaultMessage={"{address}"}
                    values={{
                      address: rentalArea?.address2
                        ? `${rentalArea.address1}, ${rentalArea.address2}`
                        : rentalArea.address1,
                    }}
                  />
                </Link>
              ) : (
                <FormattedMessage
                  defaultMessage={"{address}"}
                  values={{
                    address: rentalArea?.address2
                      ? `${rentalArea.address1}, ${rentalArea.address2}`
                      : rentalArea.address1,
                  }}
                />
              )}
            </Text>
          </HStack>
        </VStack>

        <Spacer display={{ base: "none", lg: "block" }} />

        {rentalArea?.is_bookable_online && (
          <Button
            display={{ base: "none", lg: "flex" }}
            bg="white"
            onClick={() => {
              onFormOpen();
            }}>
            <Icon
              icon="ms_calendar_add_on"
              size="1.25rem"
              color="brandPrimary.700"
            />

            <Text ml=".5rem">
              <FormattedMessage defaultMessage="Réserver en ligne" />
            </Text>
          </Button>
        )}

        <CalendarModal
          rentalArea={rentalArea}
          setDate={setDate}
          onClick={onFormOpen}>
          {function ({ disclosure }) {
            return (
              <Button
                display={{ base: "flex", lg: "none" }}
                w="full"
                bg="white"
                onClick={disclosure.onOpen}>
                <Icon
                  icon="ms_calendar_today"
                  size="1.25rem"
                  color="brandPrimary.700"
                />

                <Text ml=".5rem">
                  <FormattedMessage defaultMessage="Voir le calendrier" />
                </Text>
              </Button>
            );
          }}
        </CalendarModal>
      </Flex>

      <HStack
        p=".5rem"
        align="start"
        borderRadius="0 0 4px 4px"
        bg="gray.50"
        gap=".5rem">
        <VStack maxW="370px" align="start" spacing=".25rem">
          <Box position="relative" alignSelf="center">
            <ModalToSeeCovers
              covers={rentalArea?.covers ?? []}
              currentCover={currentCover}
              previousCover={previousCover}
              nextCover={nextCover}>
              {function ({ disclosure }) {
                return (
                  <AspectRatio
                    ratio={{ base: 113 / 48, lg: 192 / 80 }}
                    w={{ base: "300px", lg: "370px" }}
                    h={{ base: "180px", lg: "200px" }}
                    onClick={disclosure.onOpen}>
                    <PampaImage
                      image={rentalArea?.covers?.[currentCover]}
                      alt={
                        translate(
                          rentalArea?.covers?.[currentCover]?.caption,
                        ) ?? ""
                      }
                      format={format}
                      borderRadius="4px"
                      mode="crop"
                      placeholderSrc={generateFrontPath({
                        id: "@front.internal-assets",
                        parameters: { filePath: "placeholder.svg" },
                        includeBasePath: true,
                      })}
                      _groupHover={{
                        transform: "scale(1.1)",
                      }}
                      transition="all 0.25s ease"
                      bg="gray.100"
                    />
                  </AspectRatio>
                );
              }}
            </ModalToSeeCovers>

            {rentalArea?.covers && rentalArea.covers.length > 1 && (
              <HStack bottom="5px" right="5px" position="absolute">
                <IconButton
                  variant="outline"
                  icon={
                    <Icon icon="ms_chevron_left" size="24px" color="white" />
                  }
                  aria-label={intl.formatMessage({
                    defaultMessage: "Précédent",
                  })}
                  _hover={{ bg: "none" }}
                  onClick={previousCover}
                  isDisabled={currentCover === 0}
                />

                <IconButton
                  variant="outline"
                  icon={
                    <Icon icon="ms_chevron_right" size="24px" color="white" />
                  }
                  aria-label={intl.formatMessage({
                    defaultMessage: "Suivant",
                  })}
                  _hover={{ bg: "none" }}
                  onClick={nextCover}
                  isDisabled={currentCover === rentalArea.covers.length - 1}
                />
              </HStack>
            )}
          </Box>

          {rentalArea.opening_hour && rentalArea.closing_hour && (
            <HStack>
              <Icon icon="ms_schedule" size="1.25rem" color="gray.400" />

              <Text color="gray.600">
                <FormattedMessage
                  defaultMessage="De {opening} à {closing}"
                  values={{
                    opening: rentalArea.opening_hour?.slice(0, -3),
                    closing: rentalArea.closing_hour?.slice(0, -3),
                  }}
                />
              </Text>
            </HStack>
          )}

          <HStack>
            <Icon icon="ms_group" size="1.25rem" color="gray.400" />

            <Text color="gray.600">
              <FormattedMessage
                defaultMessage="{capacity} personnes maximum"
                values={{ capacity: rentalArea.max_capacity }}
              />
            </Text>
          </HStack>

          <HStack>
            <Icon icon="ms_fullscreen" size="1.25rem" color="gray.400" />

            <Text color="gray.600">
              {rentalArea.surface ? (
                <FormattedMessage
                  defaultMessage="{surface} m²"
                  values={{ surface: rentalArea.surface }}
                />
              ) : (
                <FormattedMessage defaultMessage="Surface non renseignée" />
              )}
            </Text>
          </HStack>

          <Text color="gray.600" wordBreak="break-word" noOfLines={5}>
            {translate(rentalArea.public_description)}
          </Text>

          <ViewBookings rentalArea={rentalArea}>
            {function ({ disclosure }) {
              return (
                <Text
                  as="button"
                  py=".2rem"
                  alignSelf="flex-end"
                  onClick={disclosure.onOpen}
                  _hover={{ textDecoration: "underline" }}>
                  <FormattedMessage defaultMessage="En savoir plus" />
                </Text>
              );
            }}
          </ViewBookings>
        </VStack>

        <Box w="full" display={{ base: "none", lg: "block" }}>
          <VStack>
            <DatePicker
              rentalArea={rentalArea}
              minValue={parseDateTime(dayjs().format("YYYY-MM-DD"))}
              onClick={onFormOpen}
              onChange={(event) => {
                setDate(event.target.value);
              }}
            />

            <Alert status="info">
              <AlertIcon />

              <Text
                width="full"
                paddingLeft="1rem"
                fontSize=".95rem"
                align="left">
                <FormattedMessage defaultMessage="Les horaires affichés représentent des périodes d'indisponibilité" />
              </Text>
            </Alert>
          </VStack>

          <CreateBookings
            rentalArea={rentalArea}
            date={date}
            isOpen={isFormOpen}
            onClose={() => {
              onFormClose();
            }}
          />
        </Box>
      </HStack>
    </Box>
  );
}
